import { isFulfilled } from "@reduxjs/toolkit";
import { format } from "date-fns";
import { useFormik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { NOTIFICATION_TIME_OUT } from "../../../constants/app";
import { formatReadableTimeSlot } from "../../../services/TimesheetService";
import { useAppDispatch, useAppSelector } from "../../../store";
import { showNotificationAction } from "../../../store/application/slice";
import { submitCandidateTimeSheetThunk } from "../../../store/timesheet/thunks";
import {
  ISubmitTimesheetParams,
  TTimesheetVacancyData,
} from "../../../store/timesheet/types";
import { createValidationSchema } from "../../../utils/createValidationSchema";
import CustomButton from "../../UI/Buttons/CustomButton";
import Break from "../../UI/Icons/Break";
import LogIn from "../../UI/Icons/LogIn";
import LogOut from "../../UI/Icons/LogOut";
import RoundedTick from "../../UI/Icons/RoundedTick";
import Warning from "../../UI/Icons/Warning";
import TimePicker12Hr from "../../UI/Inputs/TimePicker";
import PopupModal from "../../UI/Popup";
import ToggleButton, { ToggleOption } from "../../UI/ToggleButton";
import ErrorMessage from "../../UI/Tooltips/ErrorMessage";
import CentreImage from "../../Vacancy/CentreImage";

interface ReviewTimeSheetProps {
  /**
   * Data for the timesheet vacancy to be displayed
   * @type {TTimesheetVacancyData | null}
   */
  timeSheetData?: TTimesheetVacancyData | null;
  /**
   * Function to format job time from start and end times
   * @param job_start_time
   * @param job_end_time
   * @returns formatted job time string
   */
  formatJobTime?: (
    job_start_time: string | null,
    job_end_time: string | null
  ) => string;
  /**
   * Function to get time from date string
   * @param date
   * @param is24HourFormat
   * @returns formatted time string
   */
  setShowReviewModal: (value: boolean) => void;
  /**
   * Modal visibility state
   */
  showReviewModal: boolean;
  /**
   * Function to load the active timesheet
   * @returns Promise<void>
   */
  loadActiveTimesheet?: () => Promise<void>;
  /**
   * Function to set the field value in the form
   * @param field
   * @param value
   */
  getTimeFromDate: (date?: string | null, is24HourFormat?: boolean) => string;
  /**
   * Function to get date from date string
   * @param dateString
   * @returns formatted date string
   */
  getDateFromDateString?: (dateString: string) => string;
  /**
   * Function to get UTC formatted date and time
   * @param datestr
   * @param timestr
   * @returns formatted UTC date and time string
   */
  getUtcFormatDateTime?: (
    datestr: string | null,
    timestr: string | null
  ) => string;
}

/**
 * ReviewTimeSheet component allows a candidate to review and revise a declined timesheet.
 *
 * This modal displays the details of a declined timesheet, including job timeframe, centre information,
 * decline reason, and allows the candidate to edit and resubmit the timesheet details (job start/end time, break times).
 * The form is validated and disables submission if unchanged or invalid.
 *
 * @component
 * @param {ReviewTimeSheetProps} props - The props for the component.
 * @param {any} props.timeSheetData - The timesheet data object to review and edit.
 * @param {(start: string | null, end: string | null) => string} props.formatJobTime - Formats the job timeframe for display.
 * @param {(show: boolean) => void} props.setShowReviewModal - Function to control the visibility of the review modal.
 * @param {boolean} props.showReviewModal - Whether the review modal is visible.
 * @param {() => void} props.loadActiveTimesheet - Callback to reload the active timesheet after submission.
 * @param {(date: string, is24Hour?: boolean) => string} props.getTimeFromDate - Extracts time from a date string.
 * @param {(dateString: string) => string} props.getDateFromDateString - Extracts date from a date string.
 * @param {(date: string | null, time: string | null) => string} props.getUtcFormatDateTime - Combines date and time into a UTC datetime string.
 *
 * @returns {JSX.Element} The rendered ReviewTimeSheet modal component.
 */
const ReviewTimeSheet: React.FC<ReviewTimeSheetProps> = ({
  timeSheetData,
  formatJobTime,
  setShowReviewModal,
  showReviewModal,
  loadActiveTimesheet,
  getTimeFromDate,
  getDateFromDateString,
  getUtcFormatDateTime,
}) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const [actualJobStartTime, setActualJobStartTime] = useState<string>("");
  const [actualJobEndTime, setActualJobEndTime] = useState<string>("");
  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [iDidNotWorkOnThisDay, setIDidNotWorkOnThisDay] = useState<boolean>(false);
  const [selectedWorkType, setSelectedWorkType] = useState('worked');
  const [savedTimeValues, setSavedTimeValues] = useState({
    job_start_time: "",
    job_end_time: "",
    break_start_time: "",
    break_end_time: "",
  });

  const options: ToggleOption[] = [
    { label: 'I did work', value: 'worked' },
    { label: 'I did not work on this day', value: 'not-worked' }
  ];

  // Function to handle changes in the select fields
  // This updates the form field value based on the selected option
  const handleChange = (
    field: string,
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setFieldValue(field, e.target.value);
  };

  // Function to handle the close action of the modal
  const onClickClose = () => {
    setShowReviewModal && setShowReviewModal(false);
    setSelectedWorkType('worked'); // Reset toggle state
    setIDidNotWorkOnThisDay(false); // Reset state
    setSavedTimeValues({ // Reset saved time values
      job_start_time: "",
      job_end_time: "",
      break_start_time: "",
      break_end_time: "",
    });
  };

  // Helper to get time from date string
  const getDateFromDate = (dateString: string | null) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    // Get the date in the format "YYYY-MM-DD"
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-based
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Set initial values for the form fields based on timeSheetData
  // This ensures that the form is pre-populated with the correct times
  useEffect(() => {
    setFieldValue(
      "job_start_time",
      getTimeFromDate(timeSheetData?.job_start_time ?? "", true)
    );
    setFieldValue(
      "job_end_time",
      getTimeFromDate(timeSheetData?.job_end_time ?? "", true)
    );
    setFieldValue(
      "break_start_time",
      getTimeFromDate(timeSheetData?.break_start_time ?? "", true)
    );
    setFieldValue(
      "break_end_time",
      getTimeFromDate(timeSheetData?.break_end_time ?? "", true)
    );
  }, [timeSheetData, getTimeFromDate]);

  const jobDate =
    getDateFromDateString &&
    getDateFromDateString(timeSheetData?.job_start_time ?? "");
  const jobEndDate =
    getDateFromDateString &&
    getDateFromDateString(timeSheetData?.job_start_time ?? "");
  const jobStartDateTime =
    getUtcFormatDateTime &&
    getUtcFormatDateTime(
      jobDate ?? null,
      timeSheetData?.timeframe?.start_time ?? null
    );

  const jobEndDateTime =
    getUtcFormatDateTime &&
    getUtcFormatDateTime(
      jobEndDate ?? null,
      timeSheetData?.timeframe?.end_time ?? null
    );

  const jobTimeFrame =
    formatJobTime &&
    formatJobTime(jobStartDateTime ?? null, jobEndDateTime ?? null);

  // Set actual job start and end times based on the job start and end date times
  // This ensures that the times are formatted correctly for display
  // and validation
  useEffect(() => {
    setActualJobStartTime(
      jobStartDateTime ? getTimeFromDate(jobStartDateTime, true) : ""
    );
    setActualJobEndTime(
      jobEndDateTime ? getTimeFromDate(jobEndDateTime, true) : ""
    );
  }, [jobStartDateTime, jobEndDateTime, getTimeFromDate]);

  // Helper to extract initial time values from timeSheetData
  const getInitialTimes = () => ({
    job_start_time: getTimeFromDate(timeSheetData?.job_start_time ?? "", true),
    job_end_time: getTimeFromDate(timeSheetData?.job_end_time ?? "", true),
    break_start_time: getTimeFromDate(
      timeSheetData?.break_start_time ?? "",
      true
    ),
    break_end_time: getTimeFromDate(timeSheetData?.break_end_time ?? "", true),
  });

  /**
   * Initializes Formik for the timesheet review form, including validation, initial values, and submit handler.
   *
   * @remarks
   * - Uses a dynamic validation schema based on the timesheet and job start/end times.
   * - Sets initial values using `getInitialTimes`, and includes candidate and vacancy IDs.
   * - On submit, constructs a payload with formatted job and break times, then dispatches the timesheet submission thunk.
   * - Handles successful submission by closing the review modal, resetting the form, showing a notification, and reloading the active timesheet after a timeout.
   *
   * @param validationSchema - The schema for validating form fields, created with `createValidationSchema`.
   * @param initialValues - The initial values for the form, including times and IDs.
   * @param onSubmit - Async handler that formats and submits the timesheet data, then manages UI state and notifications.
   * @returns Formik helpers: `handleSubmit`, `values`, `errors`, `setFieldValue`, and `resetForm`.
   */
  const { handleSubmit, values, errors, setFieldValue, resetForm } =
    useFormik<ISubmitTimesheetParams>({
      validationSchema: iDidNotWorkOnThisDay ? undefined : createValidationSchema(
        "candidate_log_timesheet",
        actualJobStartTime,
        actualJobEndTime
      ),
      initialValues: {
        ...getInitialTimes(),
        candidate_id: user?.uuid || "",
        vacancy_id: timeSheetData?.vacancy_id || "",
        timeframe_id: timeSheetData?.vacancy_timeframe_id || 0
      },
      onSubmit: async (values: ISubmitTimesheetParams) => {
        if (!timeSheetData) return; // No timesheet to log
        const payload: ISubmitTimesheetParams = {
          job_start_time:
            getDateFromDate(timeSheetData.job_start_time ?? null) +
            " " +
            values.job_start_time,
          job_end_time:
            getDateFromDate(timeSheetData.job_start_time ?? null) +
            " " +
            values.job_end_time,
          break_start_time:
            values.break_start_time &&
            getDateFromDate(timeSheetData.job_start_time ?? null) +
              " " +
              values.break_start_time,
          break_end_time:
            values.break_end_time &&
            getDateFromDate(timeSheetData.job_start_time ?? null) +
              " " +
              values.break_end_time,
          vacancy_id: timeSheetData?.vacancy_id || "",
          candidate_id: user?.uuid || "",
          timeframe_id: timeSheetData?.vacancy_timeframe_id || 0,
          did_not_work: iDidNotWorkOnThisDay,
          timesheet_date: timeSheetData?.timesheet_date || "",
        };
        const response = await dispatch(submitCandidateTimeSheetThunk(payload));
        if (isFulfilled(response)) {
          setShowReviewModal && setShowReviewModal(false);
          resetForm();
          setSelectedWorkType('worked');
          setIDidNotWorkOnThisDay(false);
          dispatch(showNotificationAction("Submitted"));
          setTimeout(() => {
            loadActiveTimesheet && loadActiveTimesheet();
          }, NOTIFICATION_TIME_OUT);
        }
      },
    });

  // Store initial values in a ref
  const initialValuesRef = useRef(getInitialTimes());
  // Compute isDisabled by comparing current values to initial values
  // Update isDisabled state whenever form values change
  useEffect(() => {
    // If checkbox is checked, enable submit button
    if (iDidNotWorkOnThisDay) {
      setIsDisabled(false);
      return;
    }
    
    const isUnchanged =
      values.job_start_time === initialValuesRef.current.job_start_time &&
      values.job_end_time === initialValuesRef.current.job_end_time &&
      values.break_start_time === initialValuesRef.current.break_start_time &&
      values.break_end_time === initialValuesRef.current.break_end_time;
    const hasErrors = Object.keys(errors).length > 0;
    setIsDisabled(isUnchanged || hasErrors);
  }, [
    values.job_start_time,
    values.job_end_time,
    values.break_start_time,
    values.break_end_time,
    timeSheetData,
    errors,
    iDidNotWorkOnThisDay,
  ]);

  // Update ref if timeSheetData changes
  useEffect(() => {
    initialValuesRef.current = getInitialTimes();
  }, [timeSheetData]);

  const handleTimeChange = (value: string | null, fieldName: string) => {
    if (value) {
      // setTime12hr(value);
      setFieldValue(fieldName, value); // Convert to 24-hour format
    }
  };

  const handleWorkTypeToggle = (value: string | number) => {
    const newValue = String(value);
    setSelectedWorkType(newValue);
    
    if (newValue === 'not-worked') {
      // Save current values before clearing
      setSavedTimeValues({
        job_start_time: values.job_start_time,
        job_end_time: values.job_end_time,
        break_start_time: values.break_start_time || "",
        break_end_time: values.break_end_time || "",
      });
      
      // Clear all time fields
      setFieldValue("job_start_time", "", false);
      setFieldValue("job_end_time", "", false);
      setFieldValue("break_start_time", "", false);
      setFieldValue("break_end_time", "", false);
      
      setIDidNotWorkOnThisDay(true);
    } else {
      // Restore saved values when switching back to 'worked'
      setFieldValue("job_start_time", savedTimeValues.job_start_time, false);
      setFieldValue("job_end_time", savedTimeValues.job_end_time, false);
      setFieldValue("break_start_time", savedTimeValues.break_start_time, false);
      setFieldValue("break_end_time", savedTimeValues.break_end_time, false);
      
      setIDidNotWorkOnThisDay(false);
    }
  };

  return (
    <>
      <PopupModal
        visible={showReviewModal}
        showCloseBtn={true}
        onClickClose={onClickClose}
        customWidth={650}
        popUpClass="mobile-model"
      >
        <div className="d-flex flex-column gap-15">
          <div className="fs-22 fw-400 text-black">
            Review Declined Timesheet
          </div>
          <div className="bg-off-white h-1px"></div>
          <div className="d-flex flex-column gap-15 mobile-job-scroll">
            <div className="d-flex gap-20">
              <div className="w-50px h-50px  border-2 border-grey-dark radius-40px">
                <CentreImage
                  imageUrl={timeSheetData?.centre?.photo ?? undefined}
                  width="50px"
                  height="50px"
                  isCandidateFilledAllForms={true}
                />
              </div>
              <div className="flex-grow-1 d-flex flex-column gap-20">
                <div className="flex-grow-1 d-flex flex-column gap-3 text-break">
                  <div className="fs-16 fw-700 text-black">
                    {timeSheetData?.centre?.business_name},{" "}
                    <span className="fw-400">
                      {timeSheetData?.centre?.address}
                    </span>
                  </div>
                  <div className="fs-13 fw-400 text-black">
                    {timeSheetData?.vacancy?.matching_position?.name || ""}
                  </div>
                </div>
                <div className="d-flex flex-column gap-3 fs-13 text-black">
                  <div className="fw-700">Job Timeframe</div>
                  <div className="fw-400">
                    { timeSheetData !== null ? formatReadableTimeSlot(
                      format(new Date(timeSheetData?.job_start_time ?? ""), "yyyy-MM-dd"), 
                      timeSheetData?.timeframe?.start_time || "", 
                      timeSheetData?.timeframe?.end_time || "")
                    : ""}
                  </div>
                  {/* <div className="fw-400">{jobTimeFrame}</div> */}
                </div>
              </div>
            </div>
            <div className="d-flex">
              <div className="bg-teal-light px-15px py-10px radius-8px d-flex gap-6 align-items-center fs-13 fw-400">
                <Warning />
                Your timesheet has been declined. Please review the comments
                below shared by the centre and revise the details where
                necessary before submitting.
              </div>
            </div>
            <div className="d-flex gap-10 align-items-center">
              <div className="fs-16 fw-700 text-black">Status:</div>
              <div className="border border-alert radius-8px px-8px py-4px fs-13 fw-400 text-alert">
                Declined
              </div>
            </div>
            <div className="d-flex flex-column gap-5">
              <div className="fs-16 fw-700 text-black">Centre Comment:</div>
              <div className="fs-16 fw-400 text-black">
                {timeSheetData?.decline_reason?.message ||
                  "No comments provided."}
              </div>
            </div>
            <div className="h-1px"></div>
            <div className="fs-16 fw-700 text-black">
              Revise the details for the above job:
            </div>
            <div className="h-1px"></div>
            
            <ToggleButton
              options={options}
              selectedValue={selectedWorkType}
              onChange={handleWorkTypeToggle}
            />
            {selectedWorkType === 'worked' && (
              <>
                <div className="d-flex flex-column">
              <div className="d-flex gap-15 gap-mobile-8 align-items-center">
                <LogIn />
                <div className="fs-16 fw-400 text-black min-w-140px mobile-min-w-65px">
                  Job start <span className="d-none d-lg-inline">time</span>
                </div>
                <div className="w-120px mobile-w-95px">
                  {/* <TimeSelect
                    className="form-select appearance-none"
                    isValue24HourFormat={true}
                    onChange={(event) => handleChange("job_start_time", event)}
                    value={values?.job_start_time}
                  /> */}
                  <TimePicker12Hr
                    onChange={(value) =>
                      handleTimeChange(value as string, "job_start_time")
                    }
                    value={values?.job_start_time}
                    className="form-select appearance-none"
                  />
                </div>
              </div>
              {errors && (
                <ErrorMessage message={errors?.job_start_time as string} />
              )}
            </div>
            <div className="d-flex flex-column">
              <div className="d-flex gap-15 gap-mobile-8 align-items-center">
                <Break />
                <div className="d-flex flex-column min-w-140px mobile-min-w-65px">
                  <div className="fs-16 fw-400 text-black">
                    Break <span className="d-none d-lg-inline">timeframe</span>
                  </div>
                  <div className="fs-13 fw-400 text-grey-medium">
                    (Optional)
                  </div>
                </div>
                <div className="w-120px mobile-w-95px">
                  {/* <TimeSelect
                    className="form-select appearance-none"
                    onChange={(event) =>
                      handleChange("break_start_time", event)
                    }
                    isValue24HourFormat={true}
                    value={values?.break_start_time}
                  /> */}
                  <TimePicker12Hr
                    onChange={(value) =>
                      handleTimeChange(value as string, "break_start_time")
                    }
                    value={values?.break_start_time || ""}
                    className="form-select appearance-none"
                  />
                </div>
                <div className="fs-16 fw-400 text-black">-</div>
                <div className="w-120px mobile-w-95px">
                  {/* <TimeSelect
                    className="form-select appearance-none"
                    onChange={(event) => handleChange("break_end_time", event)}
                    isValue24HourFormat={true}
                    value={values?.break_end_time}
                  /> */}
                  <TimePicker12Hr
                    onChange={(value) =>
                      handleTimeChange(value as string, "break_end_time")
                    }
                    value={values?.break_end_time || ""}
                    className="form-select appearance-none"
                  />
                </div>
              </div>
              {errors && (
                <ErrorMessage message={errors?.break_start_time as string} />
              )}
              {errors && (
                <ErrorMessage message={errors?.break_end_time as string} />
              )}
            </div>
            <div className="d-flex flex-column">
              <div className="d-flex gap-15 gap-mobile-8 align-items-center">
                <LogOut color="#167E76" />
                <div className="fs-16 fw-400 text-black min-w-140px mobile-min-w-65px">
                  Job end <span className="d-none d-lg-inline">time</span>
                </div>
                <div className="w-120px mobile-w-95px">
                  {/* <TimeSelect
                    className="form-select appearance-none"
                    isValue24HourFormat={true}
                    onChange={(event) => handleChange("job_end_time", event)}
                    value={values?.job_end_time}
                  /> */}
                  <TimePicker12Hr
                    onChange={(value) =>
                      handleTimeChange(value as string, "job_end_time")
                    }
                    value={values?.job_end_time || ""}
                    className="form-select appearance-none"
                  />
                </div>
              </div>
              {errors && (
                <ErrorMessage message={errors?.job_end_time as string} />
              )}
            </div>
              </>
            )}
            <div className="d-flex justify-content-end align-items-center mt-10">
              <CustomButton
                variant={"primary"}
                type="button"
                className="btn-theme bg-teal text-black mobile-w-100"
                onClick={() => handleSubmit()}
                disabled={isDisabled}
              >
                <RoundedTick color={isDisabled ? "#aaaaaa" : "black"} />
                Submit
              </CustomButton>
            </div>
          </div>
        </div>
      </PopupModal>
    </>
  );
};

export default ReviewTimeSheet;
